/**
 * Renders article HTML with an in-article ad placeholder injected after every 3rd <p>.
 * Simple string-based injection keeps rendering synchronous and preserves HTML safety.
 */
import { useMemo } from 'react';
import AdSlot from '@/components/AdSlot';
import PollEmbed from './PollEmbed';

const POLL_RE = /\[poll:([a-z0-9-]+)\]/gi;

function splitByParagraphs(html) {
  // Split keeping delimiters; works for typical TipTap output with <p>…</p> blocks.
  const parts = (html || '').split(/(<\/p>)/i);
  const blocks = [];
  let buf = '';
  for (let i = 0; i < parts.length; i++) {
    buf += parts[i];
    if (/<\/p>/i.test(parts[i])) { blocks.push(buf); buf = ''; }
  }
  if (buf.trim()) blocks.push(buf);
  return blocks;
}

function renderWithPolls(chunk, keyPrefix, proseCls) {
  const matches = [...chunk.matchAll(POLL_RE)];
  if (matches.length === 0) {
    return <div className={proseCls} dangerouslySetInnerHTML={{ __html: chunk }} />;
  }
  const parts = [];
  let lastIdx = 0;
  matches.forEach((m, idx) => {
    const before = chunk.slice(lastIdx, m.index);
    if (before) parts.push(<div key={`${keyPrefix}-t-${idx}`} className={proseCls} dangerouslySetInnerHTML={{ __html: before }} />);
    parts.push(<PollEmbed key={`${keyPrefix}-p-${idx}`} pollId={m[1]} />);
    lastIdx = m.index + m[0].length;
  });
  const tail = chunk.slice(lastIdx);
  if (tail) parts.push(<div key={`${keyPrefix}-tail`} className={proseCls} dangerouslySetInnerHTML={{ __html: tail }} />);
  return <>{parts}</>;
}

export default function ArticleBody({ html, paywalled }) {
  const chunks = useMemo(() => splitByParagraphs(html), [html]);
  const proseCls = `prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-red-600 prose-strong:text-gray-900 dark:prose-strong:text-gray-100`;

  if (paywalled) {
    return <div className={`${proseCls} max-h-[300px] overflow-hidden`} dangerouslySetInnerHTML={{ __html: html }} data-testid="article-body" />;
  }

  return (
    <div data-testid="article-body">
      {chunks.map((chunk, i) => {
        const needsAd = (i + 1) % 3 === 0 && i !== chunks.length - 1;
        return (
          <div key={i}>
            {renderWithPolls(chunk, `c${i}`, proseCls)}
            {needsAd && (
              <div className="my-6 flex justify-center" data-testid={`article-inline-ad-${i}`}>
                <AdSlot size="580x300" position="article-inline" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
